/**
 * Client/Server Message Contracts
 *
 * All WebSocket communication between the browser frontend and the authoritative
 * backend uses these versioned message contracts. The backend is the sole source
 * of truth for game state; the client sends intentions and receives state updates.
 *
 * Every message includes a `version` field for future compatibility and a
 * `messageType` discriminator for routing.
 */

import { type QueueMode } from '../models/QueueModes';

// ─── Versioning ──────────────────────────────────────────────────────────────

/** Current protocol version. */
export const PROTOCOL_VERSION = '1.0.0';

/**
 * Splits a semver string into [major, minor, patch] numbers.
 */
export function parseVersion(version: string): [number, number, number] {
  const parts = version.split('.').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    throw new Error(`Invalid version string: "${version}"`);
  }
  return [parts[0], parts[1], parts[2]];
}

/**
 * Returns true if two protocol versions are compatible.
 * Major versions must match exactly; minor/patch may differ.
 */
export function isVersionCompatible(a: string, b: string): boolean {
  const [majorA] = parseVersion(a);
  const [majorB] = parseVersion(b);
  return majorA === majorB;
}

// ─── Base Message Types ──────────────────────────────────────────────────────

/**
 * Base structure for all client-to-server messages.
 */
export interface ClientMessage {
  version: string;
  messageType: string;
}

/**
 * Base structure for all server-to-client messages.
 */
export interface ServerMessage {
  version: string;
  messageType: string;
}

// ─── Discriminated Union Types ───────────────────────────────────────────────

/** All possible client-to-server message types. */
export type ClientToServerMessage =
  | AuthenticateRequest
  | JoinQueueRequest
  | StartSoloMatchRequest
  | LeaveQueueRequest
  | PlayMinionRequest
  | SellMinionRequest
  | GiveMinionRequest
  | PlaySpellRequest
  | RepositionMinionsRequest
  | BuyCardRequest
  | UpgradeMinionRequest
  | GetUpgradeOptionsRequest
  | ReconnectRequest
  | GetAchievementsRequest
  | GetStatsRequest
  | GetSkinsRequest
  | SetSkinRequest
  | BuyRuneRequest
  | MarkReadyRequest
  | GetToplistRequest
  | ResignMatchRequest
  | CreateCoopLobbyRequest
  | JoinCoopLobbyRequest
  | LeaveCoopLobbyRequest
  | SelectCoopMissionRequest
  | SelectCoopSideRequest
  | StartCoopLobbyRequest
  | CreateLobbyRequest
  | JoinLobbyRequest
  | LeaveLobbyRequest
  | KickLobbyPlayerRequest
  | StartLobbyCountdownRequest
  | GetLobbyStateRequest;

/** All possible server-to-client message types. */
export type ServerToClientMessage =
  | Authenticated
  | AuthFailed
  | QueueStatusUpdate
  | CoopLobbyStateUpdate
  | LobbyStateUpdate
  | MatchStarted
  | GameStateUpdate
  | PreparationTimerUpdate
  | ReadyStateUpdate
  | CombatStarted
  | CombatStream
  | CombatAttackEvent
  | CombatDamageEvent
  | CombatDeathEvent
  | CombatKillGoldEvent
  | CombatAbilityEvent
  | CombatResult
  | RoundStarted
  | RoundTransition
  | GameOver
  | SpectatorCombatSelected
  | SitOutNotification
  | ReconnectState
  | UpgradeOptionsResponse
  | AchievementsResponse
  | AchievementUnlocked
  | StatsResponse
  | SkinsResponse
  | ToplistResponse
  | ErrorResponse;

// ─── Client-to-Server Messages (Player Intentions) ──────────────────────────

/**
 * Sent by a logged-in client right after the socket opens (and again after
 * every reconnect) to authenticate the connection with a session token
 * obtained via the /api/auth/login or /api/auth/register endpoints.
 *
 * Guest clients never send this message — they join the queue directly with
 * a generated guest player ID.
 */
export interface AuthenticateRequest extends ClientMessage {
  messageType: 'AuthenticateRequest';
  token: string; // Session token issued by the auth endpoints
}

/**
 * Sent by the client to join the matchmaking queue.
 */
export interface JoinQueueRequest extends ClientMessage {
  messageType: 'JoinQueueRequest';
  playerId: string;
  /**
   * Which queue to join: 'fast' (4 players) or 'long' (8 players) — see the
   * shared QueueModes spec. OPTIONAL only for legacy/hand-crafted messages
   * (tests): the server defaults to 'fast' when absent. A player can only be
   * in ONE queue at a time (the server rejects a join while already queued).
   */
  queueMode?: QueueMode;
}

/**
 * Sent by the client to leave the matchmaking queue.
 */
export interface LeaveQueueRequest extends ClientMessage {
  messageType: 'LeaveQueueRequest';
  playerId: string;
}

/**
 * Sent by the client to play a minion from hand to the battlefield.
 */
export interface PlayMinionRequest extends ClientMessage {
  messageType: 'PlayMinionRequest';
  matchId: string;
  playerId: string;
  minionInstanceId: string;
  targetPosition?: number; // Optional: specific battlefield position (0-indexed). Defaults to end.
}

/**
 * Sent by the client to sell a minion from the battlefield.
 */
export interface SellMinionRequest extends ClientMessage {
  messageType: 'SellMinionRequest';
  matchId: string;
  playerId: string;
  minionInstanceId: string;
}

/**
 * Sent by the client to GIVE one of its minions (battlefield or hand) to
 * its COOP teammate for COOP_GIVE_COST gold — the sell zone's second
 * option. Only valid in a COOP match (the server rejects it elsewhere);
 * the minion is transferred to the teammate's hand (a transfer, not a
 * play: no effects, no Improved-counter change).
 */
export interface GiveMinionRequest extends ClientMessage {
  messageType: 'GiveMinionRequest';
  matchId: string;
  playerId: string;
  minionInstanceId: string;
}

/**
 * Sent by the client to CAST a spell from the hand: the spell's effect goes
 * into play immediately and the card despawns (it NEVER lands on the
 * battlefield — spells are cast, minions are played). Targeted spells (the
 * shared SpellDefinitions spec's 'minion' target — e.g. Power touch) carry
 * the target's instance ID: a friendly minion on the caster's battlefield
 * (the client casts by dragging the card onto the minion). 'battlefield'
 * spells (e.g. Healing Rain) omit it — the cast fires from anywhere over
 * the battlefield. The server validates and applies the effect
 * authoritatively (PlayerState.castSpell is the single mutation point); the
 * resulting state propagates via the usual GameStateUpdate — no dedicated
 * response message.
 */
export interface PlaySpellRequest extends ClientMessage {
  messageType: 'PlaySpellRequest';
  matchId: string;
  playerId: string;
  spellInstanceId: string;
  /** The target for 'minion'-target spells (required there). */
  targetInstanceId?: string;
}

/**
 * Sent by the client to reorder minions on the battlefield.
 */
export interface RepositionMinionsRequest extends ClientMessage {
  messageType: 'RepositionMinionsRequest';
  matchId: string;
  playerId: string;
  newOrder: string[]; // Array of minion instance IDs in desired order
}

/**
 * Sent by the client to buy a new (random) card into the hand during
 * preparation. The cost is computed authoritatively by the server: base
 * 5 gold, minus 1 per friendly Discount minion in play (stacking), never
 * below 2 gold. The card is drawn from the standard draw pool and enters
 * the hand (the hand must not be full). The resulting state is propagated
 * via the usual GameStateUpdate — no dedicated response message.
 */
export interface BuyCardRequest extends ClientMessage {
  messageType: 'BuyCardRequest';
  matchId: string;
  playerId: string;
}

/**
 * Sent by the client to upgrade a minion on the battlefield.
 */
export interface UpgradeMinionRequest extends ClientMessage {
  messageType: 'UpgradeMinionRequest';
  matchId: string;
  playerId: string;
  minionInstanceId: string;
  branchId: string; // The upgrade branch ID to apply
  selectedAbilityId?: string; // For 'ability' branch: the specific ability ID shown to the player
}

/**
 * Sent by the client when reconnecting after a connection loss.
 */
export interface ReconnectRequest extends ClientMessage {
  messageType: 'ReconnectRequest';
  playerId: string;
  matchId: string;
}

/**
 * Sent by the client to request available upgrade options for a minion.
 * The server responds with UpgradeOptionsResponse including the pre-selected
 * ability (randomly chosen) so the player knows exactly what they'll get.
 */
export interface GetUpgradeOptionsRequest extends ClientMessage {
  messageType: 'GetUpgradeOptionsRequest';
  matchId: string;
  playerId: string;
  minionInstanceId: string;
}

/**
 * Sent by a logged-in client to open the achievements page (only available
 * outside of a game). The server resolves the authoritative player identity
 * (authenticated sockets are bound to their username) and responds with
 * AchievementsResponse. Guests/unauthenticated clients receive an error.
 */
export interface GetAchievementsRequest extends ClientMessage {
  messageType: 'GetAchievementsRequest';
  playerId: string;
}

/**
 * Sent by a logged-in client to open the stats page (only available
 * outside of a game). The server resolves the authoritative player identity
 * (authenticated sockets are bound to their username) and responds with
 * StatsResponse. Guests/unauthenticated clients receive an error.
 */
export interface GetStatsRequest extends ClientMessage {
  messageType: 'GetStatsRequest';
  playerId: string;
}

/**
 * Sent by a logged-in client to open the skins page (only available
 * outside of a game). The server responds with SkinsResponse: the full
 * skin catalogue (dynamic — one entry per defined skin, grouped by class
 * on the client) with per-user `unlocked` and `selected` flags.
 * Guests/unauthenticated clients receive an error.
 */
export interface GetSkinsRequest extends ClientMessage {
  messageType: 'GetSkinsRequest';
  playerId: string;
}

/**
 * Sent by ANY client (logged-in users AND guests — the Toplist page is
 * available to everyone, outside of a game) to open the Top 100 page. The
 * server answers with ToplistResponse: the top 100 registered players by
 * rating plus, for logged-in sockets only, the requester's own placement
 * (even when outside the top 100). Guests are never placed (no rating).
 */
export interface GetToplistRequest extends ClientMessage {
  messageType: 'GetToplistRequest';
  playerId: string;
}

/**
 * Sent by a logged-in client to select the skin used for one minion class.
 * The server validates authoritatively (skin exists, belongs to the class,
 * is unlocked for the user — default skins are always unlocked) and
 * persists the choice; on success it answers with the updated SkinsResponse.
 * A locked, not-yet-unlocked skin is rejected with an ErrorResponse.
 */
export interface SetSkinRequest extends ClientMessage {
  messageType: 'SetSkinRequest';
  playerId: string;
  minionType: string; // The minion class the skin applies to
  skinId: string; // The skin ID to use for that class
}

/**
 * Sent by the client to buy a SPECIFIC rune from the rune shop during
 * preparation. The 🪬 Buy Rune button opens the shop overlay listing EVERY
 * rune in the catalogue with its gold cost; the client picks the rune it
 * wants to buy (`runeId`) and the server validates authoritatively:
 * preparation phase (unlocked), the rune exists in the catalogue, the player
 * has NOT purchased that rune in this game yet (each rune is buyable ONCE
 * per game — there is no fixed cap on how many DIFFERENT runes a player can
 * own, gold is the constraint), and enough gold for the rune's cost. On
 * success the gold is deducted, the rune becomes active for the rest of the
 * game (its effect applies starting from the next combat, stacking with the
 * player's other purchased runes) and the resulting state is propagated via
 * the usual GameStateUpdate — no dedicated response message (same convention
 * as BuyCardRequest).
 */
export interface BuyRuneRequest extends ClientMessage {
  messageType: 'BuyRuneRequest';
  matchId: string;
  playerId: string;
  runeId: string; // The rune to buy (a shared-catalogue rune ID)
}

/**
 * Sent by the client to TOGGLE the player's preparation-ready flag (the
 * "Ready" button in the hand area). `ready: true` marks the player ready to
 * let the round end early; `ready: false` retracts it. The server validates
 * authoritatively (match exists, player is alive and in preparation) and
 * broadcasts the updated count to every player in the match via
 * ReadyStateUpdate — no per-player response message. When EVERY living,
 * still-preparing player is ready the server starts the combat immediately
 * (the preparation timer is simply ignored); otherwise the normal countdown
 * still ends the round at 0.
 */
export interface MarkReadyRequest extends ClientMessage {
  messageType: 'MarkReadyRequest';
  matchId: string;
  playerId: string;
  ready: boolean; // true = mark ready, false = retract ready
}

/**
 * Sent by the client to RESIGN (forfeit) its current match. Allowed only
 * during the PREPARATION phase — the server rejects it during combat (the
 * client hides the settings menu while a combat is on, the server check is
 * the authoritative backstop) and once the match is over. On success the
 * player is ELIMINATED (life 0, dead): the round's pairing is re-rolled
 * without them, their scheduled opponent (when still alive) sits out that
 * round and SPECTATES instead, and the resignation counts as a LOSS for
 * their Elo rating (the loss is attributed against the scheduled opponent,
 * or against the nominal rating when the player was sitting out). The
 * resulting state is propagated via the usual GameStateUpdate / GameOver —
 * no dedicated response message.
 */
export interface ResignMatchRequest extends ClientMessage {
  messageType: 'ResignMatchRequest';
  matchId: string;
  playerId: string;
}

/**
 * Sent by the client to CREATE a private 2-player COOP lobby. The server
 * generates the invite code and assigns the lobby's mission (the host's
 * next available campaign mission — `missionId` optionally picks one of
 * the host's available missions up front; when all missions are cleared a
 * seeded random one is chosen). The resulting state arrives via
 * CoopLobbyStateUpdate — no dedicated response message.
 */
export interface CreateCoopLobbyRequest extends ClientMessage {
  messageType: 'CreateCoopLobbyRequest';
  playerId: string;
  /** Optional: one of the host's available mission ids (the lobby default otherwise). */
  missionId?: string;
}

/**
 * Sent by the client to JOIN a COOP lobby with an invite code (the second
 * seat). The server validates the code + seat and resyncs both members
 * via CoopLobbyStateUpdate.
 */
export interface JoinCoopLobbyRequest extends ClientMessage {
  messageType: 'JoinCoopLobbyRequest';
  playerId: string;
  code: string; // The 6-character invite code
}

/**
 * Sent by the client to LEAVE its COOP lobby. The host's leave removes the
 * whole lobby; a joiner's leave frees the seat. Both members are resynced
 * via CoopLobbyStateUpdate (exists: false leaves the client back on the
 * menu).
 */
export interface LeaveCoopLobbyRequest extends ClientMessage {
  messageType: 'LeaveCoopLobbyRequest';
  playerId: string;
}

/**
 * Sent by the HOST to re-pick the lobby's mission while waiting. Only
 * missions in the host's AVAILABLE list (the sequential campaign unlock)
 * are accepted — the server validates authoritatively. Both members are
 * resynced via CoopLobbyStateUpdate (the joiner's mission briefing stays
 * in sync with the host's pick).
 */
export interface SelectCoopMissionRequest extends ClientMessage {
  messageType: 'SelectCoopMissionRequest';
  playerId: string;
  lobbyCode: string;
  missionId: string;
}

/**
 * Sent by the HOST to pick their COMBAT SIDE while waiting: which half of
 * the combined team row their minions occupy ('left' → teamOrder 'AB',
 * the host's copies first/left and first to attack; 'right' → 'BA'). The
 * teammate automatically gets the other side. Both members are resynced
 * via CoopLobbyStateUpdate (hostSide).
 */
export interface SelectCoopSideRequest extends ClientMessage {
  messageType: 'SelectCoopSideRequest';
  playerId: string;
  lobbyCode: string;
  side: 'left' | 'right';
}

/**
 * Sent by the HOST to start the lobby's game (both seats must be filled).
 * On success the server creates the authoritative coop match — both
 * members receive the normal MatchStarted broadcast (the lobby itself is
 * removed; there is no dedicated response message).
 */
export interface StartCoopLobbyRequest extends ClientMessage {
  messageType: 'StartCoopLobbyRequest';
  playerId: string;
  lobbyCode: string;
}

// ─── Unified lobby messages (Original + Co-op, the new Play menu) ──────────

import type {
  LobbyGameType,
  LobbyVisibility,
} from '../models/Lobby';

/**
 * Sent by the client to CREATE a unified lobby (the Create Game screen).
 * The server normalizes + validates the settings authoritatively (the
 * ranked invariant is enforced server-side — a client `ranked: true` on a
 * private/coop lobby is coerced to false). For gameType 'coop' the request
 * is routed to the co-op lobby service (mission assignment, etc.). The
 * resulting state arrives via LobbyStateUpdate.
 */
export interface CreateLobbyRequest extends ClientMessage {
  messageType: 'CreateLobbyRequest';
  playerId: string;
  name?: string;
  gameType?: LobbyGameType;
  visibility?: LobbyVisibility;
  ranked?: boolean;
  /** 'original' only: 4 (fast) or 8 (long). */
  originalSize?: number;
  /**
   * 'original' only (task 209): a custom players-starting-life (1..10).
   * Omitted = the size-based default (6 long / 5 short). A custom value
   * makes the lobby unranked (the server enforces it).
   */
  startingLife?: number;
  /** 'coop' only: an optional mission id (the host's available list). */
  missionId?: string;
}

/**
 * Sent by the client to JOIN a lobby by its CODE — the UNIFIED entry
 * point that resolves BOTH original and co-op lobbies (no type selection).
 * The server resolves code → lobby → gameType and routes the client to the
 * right lobby UI. Repeated invalid codes are rate-limited server-side.
 */
export interface JoinLobbyRequest extends ClientMessage {
  messageType: 'JoinLobbyRequest';
  playerId: string;
  code: string;
}

/**
 * Sent by the client to LEAVE its lobby. A host leave (outside IN_GAME)
 * closes the lobby; a member leave frees the seat. Resynced via
 * LobbyStateUpdate.
 */
export interface LeaveLobbyRequest extends ClientMessage {
  messageType: 'LeaveLobbyRequest';
  playerId: string;
}

/**
 * Sent by the HOST to KICK a member. Host-only, server-validated, and
 * disabled once IN_GAME. Resynced via LobbyStateUpdate.
 */
export interface KickLobbyPlayerRequest extends ClientMessage {
  messageType: 'KickLobbyPlayerRequest';
  playerId: string;
  lobbyId: string;
  targetId: string;
}

/**
 * Sent by the HOST to start the 30-second server countdown (WAITING /
 * POST_GAME → COUNTDOWN). Resynced via LobbyStateUpdate (carrying the
 * authoritative countdownEndsAt — all clients render from it).
 */
export interface StartLobbyCountdownRequest extends ClientMessage {
  messageType: 'StartLobbyCountdownRequest';
  playerId: string;
  lobbyId: string;
}

/**
 * Sent by a client to (re)sync its lobby state (e.g. after a reload while
 * in a lobby). The server replies with a LobbyStateUpdate.
 */
export interface GetLobbyStateRequest extends ClientMessage {
  messageType: 'GetLobbyStateRequest';
  playerId: string;
  lobbyId: string;
}

// ─── Server-to-Client Messages (Authoritative State) ─────────────────────────

/**
 * Confirmation that an AuthenticateRequest token was accepted.
 * `playerId` is the authoritative player identity for this connection —
 * for logged-in users this is their username, and the server forces all
 * subsequent player actions (join queue, etc.) to use it.
 */
export interface Authenticated extends ServerMessage {
  messageType: 'Authenticated';
  playerId: string; // The authenticated username (authoritative player ID)
}

/**
 * Sent when an AuthenticateRequest token is invalid or expired (e.g., the
 * server restarted and in-memory sessions were lost, or the user logged out
 * from another tab). The client must return to the login screen.
 */
export interface AuthFailed extends ServerMessage {
  messageType: 'AuthFailed';
  error: string; // Human-readable reason
}

/**
 * The player's rune state within a match (authoritative, from the server).
 * The client renders the rune shop and the purchased-runes row from this
 * state plus the shared rune catalogue (RUNE_DEFINITIONS — the single
 * source of truth for rune content; no catalogue data crosses the wire).
 */
export interface PlayerRuneState {
  /**
   * The rune IDs purchased so far during THIS game, in purchase order.
   * Each rune appears at most once (a rune is buyable once per game; there
   * is no fixed cap — gold is the constraint). These are the ONLY active
   * runes: effects activate immediately after purchase, stack, and persist
   * for the rest of the game. Empty at the start of a game.
   */
  purchased: string[];
  /**
   * COOP only: this player's OWN purchases (the per-player cap budget —
   * "each player can buy 3", plus that player's RuneKeeper bonuses).
   * In coop matches `purchased` is the SHARED team union (both members
   * see every team rune as owned); this field keeps the buyer's personal
   * list for the "(x/N)" counter. Regular matches (and legacy servers)
   * omit it — clients fall back to `purchased`.
   */
  personalPurchased?: string[];
}

/**
 * Player data snapshot sent to the client.
 */
export interface PlayerData {
  id: string;
  life: number;
  gold: number;
  hand: MinionInHandData[];
  battlefield: MinionOnBoardData[];
  isAlive: boolean;
  phase: string; // 'preparation' | 'combat' | 'eliminated'
  currentRound: number;
  previousOpponentId: string | null;
  /**
   * UPGRADED (spell) — the player's cast count (task 196's dynamic
   * description): the next Upgraded cast on this player's minion pays
   * +8/+8 + 4×this many. The client renders the Upgraded card's
   * description with the buff it WOULD give. Optional only for protocol
   * backward compatibility (absence = 0).
   */
  upgradedSpellCasts?: number;
  /**
   * The player's DISPLAY NAME (optional — clients fall back to the id).
   * Humans: their username / guest id. COOP mission bots: the campaign
   * character's name ("Festergore", "Seren", ... — resolved by
   * CoopMissions.coopBotDisplayName; regular bots and humans are usually
   * null/absent and render by id).
   */
  displayName?: string | null;
  /**
   * The ID of the player this player will fight in the NEXT combat round.
   * The pairing is fixed at the start of the preparation phase (authoritative
   * — RoundCoordinator.determineUpcomingPairing) and included in every
   * PlayerData broadcast, so the client can show "who we fight" during the
   * prep round (red border on that player's name). null = the player sits
   * out the next round (odd-player round) or there is no upcoming combat
   * (e.g. the match just ended). Optional only for protocol backward
   * compatibility — clients treat absence as "no known opponent".
   */
  nextOpponentId?: string | null;
  /**
   * The player's card-skin selection: minion type (class) → skin ID.
   * Authoritative — read from the user database for registered users,
   * always `{}` for guests and bots (they render with default art).
   * A missing class entry means "default skin for that class".
   */
  skins: { [minionType: string]: string };
  /**
   * The player's rune state for this match (the runes purchased in this
   * game). The server always includes it; the field is optional only for
   * protocol backward compatibility (clients treat absence as "no active
   * runes").
   */
  runes?: PlayerRuneState;
  /**
   * The round (1-based) in which this player was ELIMINATED, or null while
   * alive. Authoritative — recorded by the RoundCoordinator at the moment
   * of death (combat loss/timeout = the round just played; resignation =
   * the preparation round, flagged by eliminatedDuringPreparation). Together
   * with that flag it pins the FINAL PLACEMENT to the order of death: the
   * player who survived longer is always placed above the one who died
   * earlier (gold/life never factor in), and the placement is frozen at
   * the moment of death. Optional only for protocol backward compatibility
   * (absence = unrecorded death, tied at the earliest position).
   */
  eliminationRound?: number | null;
  /**
   * True when the elimination happened during the PREPARATION phase
   * (resignation) instead of the round's combat. Only meaningful when
   * eliminationRound is set. Optional only for protocol backward
   * compatibility.
   */
  eliminatedDuringPreparation?: boolean;
  /**
   * The player's FINAL PLACEMENT (task 199): 1 = the winner (last
   * survivor), N = the first elimination. Assigned by the server exactly
   * once at MATCH END (MatchService) — the order is purely survival-based
   * ("how many other players were left alive") and the TIES (players who
   * died in the same turn) are broken RANDOMLY. Present only after the
   * match has ended (optional — pre-match clients never see it).
   */
  finalPlacement?: number | null;
  /**
   * The player's ACTIVE FIELD-SPELL CHARGES (the board spells that have
   * been cast and not yet spent): Healing Rain armed for the next combat,
   * the remaining Acid Rain ATTACK charges (0 = none), and Blizzard armed
   * for the next combat. The server always includes it; the field is
   * optional only for protocol backward compatibility (clients treat
   * absence as "no active charges"). COOP: the TEAM UNION (the arming
   * convention — both members' charges arm the team's combats), so both
   * clients see the same row. The client renders it as the small icon row
   * below the purchased-runes row (main.ts updateSpellCharges).
   */
  spellCharges?: {
    healingRain: boolean;
    acidRainAttacks: number;
    blizzard: boolean;
    /** Infestation armed for the player's next combat (task 194). */
    infestation?: boolean;
  };
}

/**
 * Minion data as it appears in the player's hand.
 */
export interface MinionInHandData {
  instanceId: string;
  baseMinionId: string;
  currentHp: number;
  attack: number;
  /** The minion's type classification (e.g., "warrior", "mage"). */
  minionType: string;
  upgradeCount: number;
  abilities: string[];
  /**
   * ENRICHMENT (spell, task 197): the per-card turn counter — how many
   * NEW turns this Enrichment card has sat in the hand (0 = drawn this
   * turn). The card's DYNAMIC description shows 1 + this value. Absent
   * (or 0) on non-Enrichment cards / older streams.
   */
  enrichmentTurns?: number;
}

/**
 * Minion data as it appears on the battlefield.
 */
export interface MinionOnBoardData extends MinionInHandData {
  battlefieldPosition: number;
  upgradePathState: string;
}

/**
 * Current queue status pushed to the waiting client.
 */
export interface QueueStatusUpdate extends ServerMessage {
  messageType: 'QueueStatusUpdate';
  playerId: string;
  /**
   * The number of players in the VIEWER'S OWN queue (the queue `queueMode`
   * names, when the viewer is queued; 0 otherwise). `playersNeeded` is the
   * complement against that queue's size (0 when not queued).
   */
  playersInQueue: number;
  playersNeeded: number;
  /**
   * The queue mode the viewer is currently in ('fast' | 'long'), or null when
   * the viewer is not in any queue (authoritative, server-side). The client
   * uses it to label the queue the viewer waits in. Absent on legacy messages
   * (treated as not queued).
   */
  queueMode?: QueueMode | null;
  /**
   * Number of players currently in the FAST queue — sent to EVERY waiting
   * client (the queue view shows both queues' sizes). Absent on legacy
   * messages (client hides the per-queue rows).
   */
  fastQueuePlayers?: number;
  /** Number of players currently in the LONG queue (same convention). */
  longQueuePlayers?: number;
  /**
   * Whether `playerId` is currently in the matchmaking queue (authoritative,
   * server-side). Sent on every broadcast — join, LEAVE, and match start —
   * because the broadcast reaches the player who left too (and every other
   * waiting client), so a client must never assume "in queue" on receipt.
   * Optional only for legacy/hand-crafted messages (tests): the client
   * falls back to the historical "in queue" assumption when absent.
   */
  isInQueue?: boolean;
  /**
   * Seconds until the bot-fill timer fires (the match auto-starts with
   * bots). PRESENT ONLY from 2 s after a join until the timer fires (the
   * server omits it during the first 2 s of a fresh/reset timer and once
   * the match has started): the client shows "Starting with Bots in: X
   * seconds" while present and hides the hint when the field is absent — a
   * join resets the timer to 30 s, so the hint disappears for 2 s, then
   * reappears at "Starting with Bots in: 28 seconds" and counts down.
   * Re-sent every second by the server clock tick while in the window.
   */
  botFillSecondsLeft?: number;
}

/**
 * The authoritative state of a COOP lobby, broadcast to BOTH members on
 * every change (create, join, mission select, leave, start). The client
 * renders the coop lobby screen purely from this message (the lobby code
 * + mission text come from here + the shared mission spec): the invite
 * code centered top, the mission briefing in the middle, Leave/Start at
 * the bottom. `exists: false` = the lobby is gone (the host left or the
 * match started) — the client returns to the menu.
 */
export interface CoopLobbyStateUpdate extends ServerMessage {
  messageType: 'CoopLobbyStateUpdate';
  lobbyCode: string;
  /** The host's player id (the host is always the match's playerA). */
  hostId: string;
  /** The host's display name. */
  hostName: string;
  /** The joiner's player id, or null while the seat is empty. */
  memberId: string | null;
  /** The joiner's display name, or null while the seat is empty. */
  memberName: string | null;
  /** The lobby's mission id (a shared-spec CoopMissions id). */
  missionId: string;
  /** The host's available mission ids (the sequential campaign unlock). */
  availableMissionIds: string[];
  /**
   * The host's NEXT mission to complete (the first uncleared one in
   * campaign order) — the lobby dropdown marks it clearly. null when
   * every mission is cleared (all are replays). Legacy messages omit it.
   */
  nextMissionId?: string | null;
  /**
   * The host's chosen combat side ('left' → teamOrder 'AB', the host's
   * copies first/left and first to attack; 'right' → 'BA'). The teammate
   * gets the other side. Legacy messages omit it (the match falls back
   * to the seeded coin flip).
   */
  hostSide?: 'left' | 'right';
  /** False = the lobby was removed (the client must return to the menu). */
  exists: boolean;
}

/**
 * Type-guard for CoopLobbyStateUpdate.
 */
export function isCoopLobbyStateUpdate(
  msg: ServerToClientMessage
): msg is CoopLobbyStateUpdate {
  if (msg.messageType !== 'CoopLobbyStateUpdate') return false;
  const m = msg as CoopLobbyStateUpdate;
  return typeof m.lobbyCode === 'string' &&
    typeof m.hostId === 'string' &&
    typeof m.hostName === 'string' &&
    (m.memberId === null || typeof m.memberId === 'string') &&
    (m.memberName === null || typeof m.memberName === 'string') &&
    typeof m.missionId === 'string' &&
    Array.isArray(m.availableMissionIds) &&
    (m.nextMissionId === undefined || m.nextMissionId === null || typeof m.nextMissionId === 'string') &&
    (m.hostSide === undefined || m.hostSide === 'left' || m.hostSide === 'right') &&
    typeof m.exists === 'boolean';
}

// ─── Unified lobby state (Original + Co-op) ──────────────────────────────

/** One player's seat, as broadcast in a LobbyStateUpdate. */
export interface LobbyPlayerInfo {
  userId: string;
  displayName: string;
  joinedAt: number;
  joinedVia: 'host' | 'code' | 'battlebrew_invite' | 'crazygames_invite' | 'ranked_queue' | 'bot';
  isHost: boolean;
}

/**
 * The authoritative state of a unified lobby, broadcast to its members on
 * every mutation (the single canonical snapshot the client renders from).
 * Carries BOTH original fields (size/ranked/countdown) and the optional
 * co-op fields (mission/side) so one message type drives both lobby UIs.
 * `exists: false` = the lobby is gone — the client returns to the menu.
 */
export interface LobbyStateUpdate extends ServerMessage {
  messageType: 'LobbyStateUpdate';
  /** The immutable internal lobby id (the CrazyGames roomId). */
  id: string;
  /** The short human-readable join code. */
  code: string;
  hostUserId: string;
  name: string;
  gameType: 'original' | 'coop';
  visibility: 'public' | 'private';
  ranked: boolean;
  playerCapacity: number;
  originalSize: 4 | 8 | null;
  /** 'original' only (task 209): the players-starting-life. null for coop. */
  startingLife: number | null;
  state: 'WAITING' | 'COUNTDOWN' | 'IN_GAME' | 'POST_GAME' | 'CLOSED';
  /** Server time the countdown ends (null outside COUNTDOWN). Authoritative
   *  for the MATCH START — the server flips to the match at this deadline. */
  countdownEndsAt: number | null;
  countdownStartedAt: number | null;
  /** The countdown's REMAINING SECONDS as computed by the SERVER (present
   *  while COUNTDOWN). The client renders from this and counts down locally
   *  (the server clock is not epoch time, so the client cannot derive the
   *  remaining seconds from countdownEndsAt alone — the botFillSecondsLeft
   *  convention). */
  countdownSecondsLeft?: number;
  players: LobbyPlayerInfo[];
  // Co-op only (absent for original lobbies):
  missionId?: string;
  availableMissionIds?: string[];
  nextMissionId?: string | null;
  hostSide?: 'left' | 'right';
  exists: boolean;
}

/**
 * Type-guard for LobbyStateUpdate.
 */
export function isLobbyStateUpdate(msg: ServerToClientMessage): msg is LobbyStateUpdate {
  if (msg.messageType !== 'LobbyStateUpdate') return false;
  const m = msg as LobbyStateUpdate;
  return (
    typeof m.id === 'string' &&
    typeof m.code === 'string' &&
    typeof m.hostUserId === 'string' &&
    (m.gameType === 'original' || m.gameType === 'coop') &&
    (m.visibility === 'public' || m.visibility === 'private') &&
    typeof m.ranked === 'boolean' &&
    Array.isArray(m.players) &&
    typeof m.exists === 'boolean'
  );
}

/**
 * Sent when a match starts, providing the full initial state.
 */
/**
 * COOP match info — carried on the match-state messages (MatchStarted /
 * GameStateUpdate / ReconnectState) when the match is a COOP match (the
 * server's single source: the coordinator's CoopMatchOptions + the shared
 * mission spec). Absent on regular matches.
 */
export interface CoopMatchInfo {
  /** The mission id (a shared CoopMissions id). */
  missionId: string;
  /** The mission's display name (the HUD title). */
  missionName: string;
  /** The mission briefing text (the lobby / results screens). */
  missionDescription: string;
  /** The combat shape ('team' | 'team2' | 'normal'). */
  mode: 'team' | 'team2' | 'normal';
  /**
   * The FIXED combined team board order (team missions only — 'AB' =
   * [host board..., teammate board...], 'BA' = the flip; the client
   * renders the lower half of the combat board in this order). 'AB' for
   * normal missions (no combined board).
   */
  teamOrder: 'AB' | 'BA';
  /** The host's player id (always the match's canonical winner). */
  hostId: string;
  /** The teammate's player id. */
  teammateId: string;
  /** The mission bot ids (campaign order). */
  enemyIds: string[];
}

export interface MatchStarted extends ServerMessage {
  messageType: 'MatchStarted';
  matchId: string;
  currentRound: number;
  phase: string;
  players: PlayerData[];
  /** COOP match info (absent on regular matches). */
  coop?: CoopMatchInfo;
}

/**
 * A preparation-phase CARD FX event (task 186) — a one-shot visual effect
 * the server observed while mutating card state, carried on the next
 * GameStateUpdate so the client can play the matching effect at the right
 * moment (the state diff alone can't distinguish "the draw was lost
 * (overdraw)" from "no draw happened", and can't say WHICH vanished hand
 * card was destroyed by the Sacrificial Rune vs played/sold).
 *
 *  - 'overdraw': a card was DRAWN for `playerId` but the hand was full —
 *    the card is lost. The client plays a RED CARD-LIKE EXPLOSION over
 *    that player's hand (ANY overdraw — with or without the Sacrificial
 *    Rune).
 *  - 'cardDestroyed': the Sacrificial Rune (or a future destroy source)
 *    destroyed hand card `instanceId` of `playerId` — the client evaporates
 *    that card into BURNING PARTICLES at its last on-screen position.
 */
/**
 * The face of a card that was LOST to an overdraw (task 201) — enough to
 * render the REAL card in the hand before it bursts: the user sees
 * exactly which card got overdrawn. Absent on legacy servers (the client
 * falls back to the strip-wide blast).
 */
export interface OverdrawCardData {
  /** The base minion definition ID (the card's art/name source). */
  baseMinionId: string;
  /** The class (mage, warrior, ... — the spell badge + class color). */
  minionType: string;
  currentHp: number;
  attack: number;
  /** Upgrades at draw time (a purchased draw is pre-leveled). */
  upgradeCount: number;
  /** Ability IDs (the keyword badges on the ghost card). */
  abilities: string[];
}

export interface CardFxEvent {
  /** The FX kind ('overdraw' | 'cardDestroyed'). */
  type: 'overdraw' | 'cardDestroyed';
  /** The player whose hand the event is about. */
  playerId: string;
  /** The destroyed hand card (cardDestroyed only). */
  instanceId?: string;
  /**
   * The LOST card's face (overdraw only, task 201): the client draws the
   * REAL card into the hand (visible, non-interactive) and destroys it
   * with the overdraw effect after a short delay — several overdraws in
   * one batch play SEQUENTIALLY (draw → destroy → next). Absent on legacy
   * servers (the strip-wide blast fallback).
   */
  card?: OverdrawCardData;
}

/**
 * Full game state update pushed after any state-changing action.
 */
export interface GameStateUpdate extends ServerMessage {
  messageType: 'GameStateUpdate';
  matchId: string;
  currentRound: number;
  phase: string;
  players: PlayerData[];
  gameOver: boolean;
  winnerId: string | null;
  /** COOP match info (absent on regular matches). */
  coop?: CoopMatchInfo;
  /**
   * One-shot card FX events observed since the last update (task 186 —
   * overdraw explosions + Sacrificial Rune hand-card evaporations).
   * Absent/empty when nothing happened.
   */
  cardEvents?: CardFxEvent[];
}

/**
 * Preparation timer countdown update.
 */
export interface PreparationTimerUpdate extends ServerMessage {
  messageType: 'PreparationTimerUpdate';
  matchId: string;
  remainingSeconds: number;
  /** Whether preparation actions are locked (during the dead-time threshold before combat). */
  isLocked?: boolean;
}

/**
 * Authoritative preparation "ready" state, broadcast to every player in the
 * match whenever someone toggles their ready flag (human or bot) and when a
 * new preparation phase begins (empty list = everyone reset to not-ready).
 * The client renders the Ready button purely from this message:
 * - `readyPlayerIds`: the IDs of all ready players still in preparation
 *   (the button's "x" part and the local player's own ready flag);
 * - `totalPlayers`: the number of living players in preparation (the "n").
 */
export interface ReadyStateUpdate extends ServerMessage {
  messageType: 'ReadyStateUpdate';
  matchId: string;
  /** Player IDs that are currently ready (alive + in preparation only). */
  readyPlayerIds: string[];
  /** Number of living players in preparation (the "n" of "x/n"). */
  totalPlayers: number;
}

/**
 * Combat phase has started between two players.
 */
export interface CombatStarted extends ServerMessage {
  messageType: 'CombatStarted';
  matchId: string;
  playerAId: string;
  playerBId: string;
  playerABattlefield: MinionOnBoardData[];
  playerBBattlefield: MinionOnBoardData[];
  firstAttackerId: string;
  /** playerA's skin selections (minion type → skin ID), authoritative. `{}` for guests/bots. */
  playerASkins: { [minionType: string]: string };
  /** playerB's skin selections (minion type → skin ID), authoritative. `{}` for guests/bots. */
  playerBSkins: { [minionType: string]: string };
  /**
   * COOP team combat ONLY: the skin selections of EVERY member of the
   * team side (player id → { minion type → skin ID }). The combined team
   * row mixes BOTH humans' minions — each minion renders with its OWNER's
   * selection (two warriors on the same row can wear different skins).
   * The seat's selection stays in playerASkins (legacy clients render the
   * whole row with it). Absent on regular combats and on the bot side.
   */
  playerASkinsByPlayer?: Record<string, { [minionType: string]: string }>;
  /**
   * playerA's ACTIVE runes: the rune IDs purchased during the current game,
   * in purchase order. Only purchased runes are active — displayed beside
   * the player's name in the combat UI. The server always includes it;
   * optional only for protocol backward compatibility (absence = none).
   */
  playerARunes?: string[];
  /** playerB's active runes (see playerARunes). */
  playerBRunes?: string[];
  /**
   * TRUE on a COOP TREASURE TURN (every 5th round of an opted-in team
   * mission): the BATTLE phase fights a random predefined treasure
   * collection INSTEAD of the mission bot (which sits this combat out —
   * its board and life are untouched). The client shows the "Treasure
   * fight!" banner for 2 seconds when the combat board loads (the same
   * overlay as the win/lose/tie result). Absent/false on other combats.
   */
  treasureFight?: boolean;
  /**
   * TRUE when ACID RAIN is active in this combat (the spell is armed on
   * either side — the caster's 4-combat charge): every attack deals 1
   * damage to ALL minions. The client shows GREEN RAIN falling across the
   * screen for the whole combat (ambient FX). Absent/false on other
   * combats.
   */
  acidRain?: boolean;
  /**
   * TRUE when BLIZZARD is active in this combat (the spell is armed on
   * either side — the caster's next-combat charge): every attack has a 25%
   * chance the target is Frozen. The client shows SNOWFLAKES WHIRLING
   * around the screen for the whole combat (ambient FX). Absent/false on
   * other combats.
   */
  blizzard?: boolean;
  /**
   * TRUE when INFESTATION is active in this combat (the spell is armed on
   * either side — the caster's next-combat charge, task 194): every minion
   * that dies is replaced in place by a Fragile Swarmer that attacks
   * immediately (the CombatInfestationEvents stream the spawns). The client
   * shows the
   * Infestation charge icon in the spell-charges row. Absent/false on
   * other combats.
   */
  infestation?: boolean;
}

/**
 * A single event within a combat stream.
 * Uses a discriminated union via messageType to identify the event type.
 */
export type CombatStreamEvent =
  | { messageType: 'CombatAttackEvent'; attackerInstanceId: string; attackerPlayerId: string; defenderInstanceId: string; defenderPlayerId: string }
  | { messageType: 'CombatDamageEvent'; attackerInstanceId: string; defenderInstanceId: string; damageToAttacker: number; damageToDefender: number; attackerHpAfter: number; defenderHpAfter: number; simultaneous?: boolean }
  | { messageType: 'CombatDeathEvent'; minionInstanceId: string; playerId: string; simultaneous?: boolean }
  | { messageType: 'CombatKillGoldEvent'; killerInstanceId: string; killerPlayerId: string; victimInstanceId: string; goldAwarded: number }
  | { messageType: 'CombatAbilityEvent'; abilityId: string; sourceInstanceId: string; sourcePlayerId: string; triggerType: string }
  | { messageType: 'CombatExplodingShimmerEvent'; playerId: string; instanceIds: string[] }
  /**
   * Aura stat changes on one side (Warband / Frenzy ATK auras; Strengthen's
   * ATK+HP aura). `attack` is the copy's NEW total attack after the change.
   * `hp` / `maxHp` (Strengthen only) are the copy's NEW current / max HP —
   * the aura adds or removes both (removal can never kill: current HP is
   * floored at 1). The combat board updates the ATK numbers and the HP bar
   * from these authoritative values.
   */
  | { messageType: 'CombatAuraEvent'; playerId: string; changes: Array<{ instanceId: string; attack: number; hp?: number; maxHp?: number }> }
  | { messageType: 'CombatNecromancyEvent'; undeadInstanceId: string; victimInstanceId: string; sourceInstanceId: string; playerId: string; position: number; attack: number; currentHp: number; abilities: string[] }
  /**
   * Infestation (spell, task 194, reworked task 196): a minion that DIED
   * in the combat has just been replaced in place by a temporary FRAGILE
   * SWARMER token on the `playerId`'s side (the dead minion's slot,
   * `position`), and the Swarmer is about to attack immediately (Fragile
   * dies it right after). Combat-only token (the `infestation_` ID prefix —
   * task 204 renamed it from the accidental `spawner_` prefix; never
   * persistent state; battlefield restoration discards it at combat end).
   * The client swaps the corpse for the Swarmer exactly when this event is
   * animated (the Necromancy swap convention), plays the spawn FX, and the
   * Swarmer's immediate attack arrives as the normal CombatAttackEvents
   * that follow in the stream. `minionType` / `abilities` carry the token's
   * identity (the Swarmer card + the Fragile badge — absent on streams
   * predating the fields, the client falls back to the Swarmer).
   */
  | { messageType: 'CombatInfestationEvent'; infestationInstanceId: string; victimInstanceId: string; playerId: string; position: number; attack: number; currentHp: number; minionType?: string; abilities?: string[] }
  /**
   * Treasure Hunter (spell, one-combat rework task 196): the killer's first
   * kill replaced the dead minion IN COMBAT, in place, with a TEMPORARY
   * treasure — the Treasure Chest item (25 HP / 0 ATK, the Treasure
   * keyword) as a `treasure_` combat token on the `playerId`'s side (the
   * victim's side). Combat-only token: battlefield restoration discards it
   * at combat end (no persistent board insertion). The enemy killing it
   * earns its 25-gold Treasure reward (the normal killGold event). The
   * client swaps the corpse for the treasure exactly when this event is
   * animated (the Necromancy/Infestation swap convention) + the spawn FX.
   */
  | { messageType: 'CombatTreasureEvent'; treasureInstanceId: string; victimInstanceId: string; playerId: string; position: number; attack: number; currentHp: number; minionType: string; abilities: string[] }
  /**
   * Shield Breaker (Warrior/Blightcaster, level 3): the `sourceInstanceId`
   * just attacked `targetInstanceId` (on the `targetPlayerId`'s side),
   * which had the SHIELD keyword (taunt) — the Shield was TEMPORARILY
   * REMOVED for the rest of this combat (the combat copy loses the 'shield'
   * ability: enemies can now target it freely) and it is RESTORED when the
   * combat ends (combat-copy-only effect). The client drops the Shield
   * badge from the card and plays the red shield-shatter particle burst on
   * the target exactly when this event is animated (the FX plays only when
   * a Shield is actually removed — a second break of the same unit never
   * streams).
   */
  | { messageType: 'CombatShieldBrokenEvent'; sourceInstanceId: string; sourcePlayerId: string; targetInstanceId: string; targetPlayerId: string }
  /**
   * LifeBurst (Healer, level 2 — task 206): the deceased holder's
   * board-wide heal just landed — every LIVING minion on BOTH sides
   * recovered HP (capped at max HP). `healed` lists each healed minion +
   * the HP it actually recovered. The per-target HP updates ride the
   * negative CombatDamageEvents; this event drives the signature FX (the
   * bright green battlefield flash + the green bursts / plus-signs over
   * the whole board).
   */
  | { messageType: 'CombatLifeBurstEvent'; sourceInstanceId: string; playerId: string; healed: Array<{ instanceId: string; healed: number }> }
  /**
   * Inspired (temporary keyword — task 207): the Hero's killing attack
   * just granted the INSPIRED keyword to the listed friendly minions
   * (combat-scoped: +2/+2, doubled to 4 → 8 on every re-grant). Each
   * target carries its NEW total attack/HP (the authoritative post-grant
   * values) + statTotal (the keyword's current boosting stats for that
   * copy). The client shows the temporary keyword badge + the stat-gain
   * FX; the post-combat state update re-initializes the board without the
   * keyword (the stats are temporary by construction).
   */
  | { messageType: 'CombatInspiredEvent'; sourceInstanceId: string; playerId: string; targets: Array<{ instanceId: string; newAttack: number; newHp: number; maxHp: number; statTotal: number }> }
  | { messageType: 'CombatSuddenDeathEvent'; attackNumber: number; damage: number }
  /**
   * Toxic Cloud (Blightcaster, level requirement 3): at the start of the
   * `affectedPlayerId`'s attack turn, every LIVING Toxic Cloud holder of
   * `sourcePlayerId` made ALL of the affected player's minions take
   * `damage` (TOXIC_CLOUD_DAMAGE × living holders — the clouds stack but
   * the cloud triggers only once per turn). The client plays the green
   * cloud over the entire affected player's battlefield; the per-minion
   * HP changes arrive as the simultaneous CombatDamageEvents that follow
   * in the stream (authoritative hpAfter values).
   */
  | { messageType: 'CombatToxicCloudEvent'; sourcePlayerId: string; affectedPlayerId: string; damage: number }
  /**
   * Mark (Blightcaster, level requirement 1): `sourceInstanceId` (the
   * attacker) marked `targetInstanceId` (the enemy it attacked) with its
   * OnAttack ability. A marked minion takes MARK_EXTRA_DAMAGE extra damage
   * every time it takes damage for the rest of this battle. The client
   * starts the continuous black-particle emitter at the target (it stops
   * when the target dies or the combat ends — the card element is removed
   * with the combat board).
   */
  | { messageType: 'CombatMarkEvent'; sourceInstanceId: string; sourcePlayerId: string; targetInstanceId: string; targetPlayerId: string }
  /**
   * Shared Pain (Blightcaster, level requirement 4): `sourceInstanceId`
   * (the holder) just took `damage` HP and every LIVING ENEMY MARKED minion
   * listed in `targetInstanceIds` takes the same amount (the mark's +3 is
   * included in the per-target CombatDamageEvents that follow). The client
   * draws the red expanding particle line from the holder to each marked
   * target that also received the damage.
   */
  | { messageType: 'CombatSharedPainEvent'; sourceInstanceId: string; sourcePlayerId: string; targetInstanceIds: string[]; damage: number }
  /**
   * Freeze (Mage/Bard, level requirement 3): `sourceInstanceId` attacked
   * `targetInstanceId` and the 50% freeze roll landed — the target is FROZEN.
   * At the moment it would have attacked it is unfrozen and skips that
   * attack (see CombatFreezeSkipEvent). The client plays the large white
   * ice/snow burst at the target when this event is animated.
   */
  | { messageType: 'CombatFreezeEvent'; sourceInstanceId: string; sourcePlayerId: string; targetInstanceId: string; targetPlayerId: string }
  /**
   * Freeze (the skip): `instanceId` (playerId) was frozen by an earlier
   * Freeze attack and, at the moment it would have attacked, is UNFROZEN and
   * skips that attack (no damage, no OnAttack abilities, no sudden-death
   * attack count — the sequence moves on to the next minion). The client
   * plays the ice-shatter burst at the unfrozen minion.
   */
  | { messageType: 'CombatFreezeSkipEvent'; instanceId: string; playerId: string }
  /**
   * SwarmLord (Mage/Blightcaster, level requirement 5): `sourceInstanceId`
   * (the SwarmLord holder that just died; playerId = its owner) triggered
   * the swarm — the holder's board was filled with fresh level-1 Swarmer
   * tokens (10 ATK / 1 HP, Glasscannon), one per empty battlefield slot
   * (left to right). The swarmers attack immediately (their attack /
   * damage / death events follow in the stream) BEFORE the opposing side's
   * next turn. The client adds each swarmer card to the owner's combat
   * board at its authoritative position and plays an egg-hatching burst on
   * EVERY spawned swarmer card.
   */
  | { messageType: 'CombatSwarmEvent'; sourceInstanceId: string; playerId: string; swarmers: Array<{ instanceId: string; position: number; attack: number; currentHp: number; abilities: string[] }> }
  /**
   * REPEATER (Spellmaker, level 3 — task 184): at the start of combat a
   * Repeater holder re-cast the last minion-targeting spell that was cast
   * ON THE HOLDER on one of its battlefield neighbors. The client plays
   * the spell-cast FX (the STAR burst — the minion-targeting-spell cast
   * effect — plus the spell sound) on the target neighbor. The spell
   * effect itself is authoritative on the server (the persistent
   * stat/keyword change rides the post-combat state update).
   */
  | { messageType: 'CombatSpellCastEvent'; sourceInstanceId: string; sourcePlayerId: string; targetInstanceId: string; targetPlayerId: string; spellId: string; grantedAbilities?: string[] }
  | { messageType: 'CombatResult'; winnerId: string | null; loserId: string | null; isTie: boolean; isTimeout: boolean; lifeLost: { [playerId: string]: number }; goldGained: { [playerId: string]: number } };

/**
 * Batched combat event stream. Sent as a single WebSocket message after
 * combat resolves, replacing the old per-event streaming approach.
 * The frontend animates events locally with its own timing delays.
 */
export interface CombatStream extends ServerMessage {
  messageType: 'CombatStream';
  matchId: string;
  /** Ordered array of combat events to animate. */
  events: CombatStreamEvent[];
}

/**
 * An attack event: one minion targets another.
 */
export interface CombatAttackEvent extends ServerMessage {
  messageType: 'CombatAttackEvent';
  matchId: string;
  attackerInstanceId: string;
  attackerPlayerId: string;
  defenderInstanceId: string;
  defenderPlayerId: string;
}

/**
 * Simultaneous damage dealt to attacker and defender.
 */
export interface CombatDamageEvent extends ServerMessage {
  messageType: 'CombatDamageEvent';
  matchId: string;
  attackerInstanceId: string;
  defenderInstanceId: string;
  damageToAttacker: number; // Damage defender deals back
  damageToDefender: number; // Damage attacker deals
  attackerHpAfter: number;
  defenderHpAfter: number;
  /**
   * True when this damage is part of a simultaneous multi-target group (e.g.,
   * the Explosive ability hitting every minion at once). Consecutive
   * simultaneous events are animated at the same instant on the frontend.
   */
  simultaneous?: boolean;
}

/**
 * A minion has died during combat.
 */
export interface CombatDeathEvent extends ServerMessage {
  messageType: 'CombatDeathEvent';
  matchId: string;
  minionInstanceId: string;
  playerId: string;
  /** True when this death is part of a simultaneous group (see CombatDamageEvent.simultaneous). */
  simultaneous?: boolean;
}

/**
 * Gold awarded for killing a minion.
 */
export interface CombatKillGoldEvent extends ServerMessage {
  messageType: 'CombatKillGoldEvent';
  matchId: string;
  killerInstanceId: string;
  killerPlayerId: string;
  victimInstanceId: string;
  goldAwarded: number;
}

/**
 * An ability triggered during combat.
 */
export interface CombatAbilityEvent extends ServerMessage {
  messageType: 'CombatAbilityEvent';
  matchId: string;
  abilityId: string;
  sourceInstanceId: string;
  sourcePlayerId: string;
  triggerType: string; // 'OnDraw' | 'OnAttack' | 'OnKill' | 'OnCombatStart' | 'OnDeath'
}

/**
 * Combat has resolved with a result.
 */
export interface CombatResult extends ServerMessage {
  messageType: 'CombatResult';
  matchId: string;
  winnerId: string | null; // null for tie
  loserId: string | null;  // null for tie
  isTie: boolean;
  isTimeout: boolean;
  lifeLost: { [playerId: string]: number }; // life lost per player (0 for winner/tie)
  goldGained: { [playerId: string]: number }; // gold gained per player
}

/**
 * A new round has started.
 */
export interface RoundStarted extends ServerMessage {
  messageType: 'RoundStarted';
  matchId: string;
  currentRound: number;
  phase: string;
  drawsThisRound: number;
  preparationTimerSeconds: number;
}

/**
 * Sent to a player whose combat has resolved but other combats in the
 * same round are still ongoing. Informs the player to wait.
 */
export interface RoundTransition extends ServerMessage {
  messageType: 'RoundTransition';
  matchId: string;
  currentRound: number;
  message: string; // Human-readable message, e.g. "Waiting for other players…"
}

/**
 * The match has ended.
 */
export interface GameOver extends ServerMessage {
  messageType: 'GameOver';
  matchId: string;
  winnerId: string;
  finalPlayers: PlayerData[];
}

/**
 * Sent to the sitting-out player indicating which combat they will spectate.
 */
export interface SpectatorCombatSelected extends ServerMessage {
  messageType: 'SpectatorCombatSelected';
  matchId: string;
  spectatorPlayerId: string;
  combatPlayerAId: string;
  combatPlayerBId: string;
}

/**
 * Sent to the sitting-out player notifying them they receive 3 gold.
 */
export interface SitOutNotification extends ServerMessage {
  messageType: 'SitOutNotification';
  matchId: string;
  playerId: string;
  goldAwarded: number;
}

/**
 * Full state sent to a reconnecting client.
 */
export interface ReconnectState extends ServerMessage {
  messageType: 'ReconnectState';
  matchId: string;
  playerId: string;
  currentRound: number;
  phase: string;
  gameOver: boolean;
  winnerId: string | null;
  players: PlayerData[];
  /** COOP match info (absent on regular matches). */
  coop?: CoopMatchInfo;
  /**
   * Current preparation ready state (only present while a preparation phase
   * is active): the ready player IDs and the number of living players in
   * preparation. Absent outside preparation — the client treats absence as
   * "not ready, 0/0" and resyncs on the next ReadyStateUpdate.
   */
  readyPlayerIds?: string[];
  readyTotalPlayers?: number;
}

/**
 * A single upgrade option returned by the server.
 */
export interface UpgradeOption {
  /** Branch ID: 'hp', 'atk', 'ability', 'bonus', 'transform' or 'high_stakes'. */
  branchId: string;
  /** Human-readable name. */
  name: string;
  /** HP change. */
  hpChange: number;
  /** Attack change. */
  attackChange: number;
  /** For 'ability' branch: the specific ability ID the server pre-selected. Empty for hp/atq. */
  abilityId: string;
  /** For 'ability' branch: the ability name. Empty for hp/atq. */
  abilityName: string;
  /**
   * For 'transform' branch ONLY: the minion class the transform turns the
   * minion into (e.g. 'runekeeper'). The client renders the transform as a
   * pulsing portal carrying this class's name; choosing it replaces the
   * minion with a fresh level-1 minion of this class (authoritative — the
   * server re-validates the shared transform spec). Absent for every other
   * branch.
   */
  transformTargetClass?: string;
  /**
   * For the Rune of High Stakes option ('high_stakes') ONLY: the chance
   * (0..1) that choosing this option DESTROYS the minion instead of
   * applying the +10/+10 — 10% × the minion's level (the shared
   * highStakesDestroyChance — the server rolls the authoritative seeded
   * RNG; this is the same number the panel displays). Absent for every
   * other branch.
   */
  destroyChance?: number;
}

/**
 * Server response to GetUpgradeOptionsRequest.
 * Contains available upgrade branches for a minion, including the
 * server-pre-selected ability (randomly chosen from eligible abilities).
 */
export interface UpgradeOptionsResponse extends ServerMessage {
  messageType: 'UpgradeOptionsResponse';
  matchId: string;
  minionInstanceId: string;
  options: UpgradeOption[];
  upgradeCost: number;
}

/**
 * Achievement data as shown to the client.
 *
 * Hidden achievements that are NOT yet unlocked are redacted server-side:
 * name '???', icon '❓', placeholder description, progress hidden. The
 * frontend renders exactly what the server sends (authoritative backend).
 */
export interface AchievementInfo {
  /** Stable achievement ID. */
  id: string;
  /** Display name (redacted for hidden-unlocked). */
  name: string;
  /** Icon (redacted for hidden-unlocked). */
  icon: string;
  /** Explaining text (placeholder for hidden-unlocked). */
  description: string;
  /** Whether this achievement is hidden (secret until unlocked). */
  hidden: boolean;
  /** Progress needed to unlock. */
  target: number;
  /** Current progress (0 for hidden-unlocked). */
  progress: number;
  /** Whether the achievement is unlocked. */
  unlocked: boolean;
  /** ISO timestamp of the unlock (null while locked). */
  unlockedAt: string | null;
}

/**
 * Response to GetAchievementsRequest: the player's full achievement list.
 * Sorted server-side: unlocked first, then closest to unlocking.
 */
export interface AchievementsResponse extends ServerMessage {
  messageType: 'AchievementsResponse';
  achievements: AchievementInfo[];
}

/**
 * Pushed to a logged-in user the moment an achievement unlocks (win,
 * elimination, minion stat, ...). The frontend shows a toast; the
 * achievements overlay (if open) refreshes. Unlocked hidden achievements
 * are revealed in full here.
 */
export interface AchievementUnlocked extends ServerMessage {
  messageType: 'AchievementUnlocked';
  achievementId: string;
  name: string;
  icon: string;
  description: string;
}

/**
 * Per-user statistics as shown to the client. Mirrors the authoritative
 * UserStats from the user database — all counters advance only through
 * server-validated paths (match start/end, play, upgrade, sell, combat
 * kill attribution).
 */
export interface PlayerStats {
  /** The username exactly as registered. */
  username: string;
  /** Matches the user entered. */
  gamesPlayed: number;
  /** Matches the user finished in 1st place. */
  wins: number;
  /** Cards played from hand to the battlefield. */
  cardsPlayed: number;
  /** Times the user reduced another player's or bot's life to 0. */
  playersEliminated: number;
  /** Minions upgraded (server-validated). */
  cardsUpgraded: number;
  /** Cards sold (from hand or battlefield). */
  cardsSold: number;
  /** Enemy minions killed by the user's minions in combat. */
  enemyCardsKilled: number;
}

/**
 * Response to GetStatsRequest: the player's current statistics.
 * Always for the socket's own username (a client cannot read another
 * user's stats).
 */
export interface StatsResponse extends ServerMessage {
  messageType: 'StatsResponse';
  stats: PlayerStats;
}

/**
 * One row of the Top 100 list (and the requester's own placement row).
 * All values are authoritative (server-resolved from the user database).
 */
export interface ToplistEntry {
  /**
   * Competition rank: 1 + the number of players with a STRICTLY higher
   * rating (ties share a rank — e.g. two players tied first are both
   * rank 1, the next player is rank 3). The same convention is used for
   * `myPlacement`, so the bottom row is always comparable to the list.
   */
  rank: number;
  /** The username exactly as registered. */
  username: string;
  /** The current rating (integer points). */
  rating: number;
}

/**
 * Response to GetToplistRequest.
 *  - `entries`: the TOP 100 registered players by rating (descending;
 *    ties broken by username for a stable order). ALWAYS served — guests
 *    can view the list too.
 *  - `myPlacement`: the requesting user's own row (same shape, same rank
 *    convention) when the socket is authenticated, `null` for guests
 *    (guests are not ranked and never appear in `entries`). Present even
 *    when the user is outside the top 100 — that is the row shown at the
 *    bottom of the page.
 */
export interface ToplistResponse extends ServerMessage {
  messageType: 'ToplistResponse';
  entries: ToplistEntry[];
  myPlacement: ToplistEntry | null;
}

/**
 * A single skin entry in the SkinsResponse, with per-user state resolved
 * server-side. The frontend renders the list grouped by `minionType` and
 * shows a lock badge when `unlocked` is false — it never decides for itself
 * what is selectable (authoritative backend).
 */
export interface SkinInfo {
  /** Stable skin ID (e.g. 'mage_archmage'). */
  id: string;
  /** The minion class this skin applies to. */
  minionType: string;
  /** Display name. */
  name: string;
  /** Image URL for the skin preview and card rendering. */
  imageUrl: string;
  /** True for the class's default skin (always unlocked/available). */
  isDefault: boolean;
  /** True when the requesting user may select this skin right now. */
  unlocked: boolean;
  /** True when this is the user's current selection for the class. */
  selected: boolean;
  /**
   * Human-readable unlock criteria for LOCKED skins (the small caption
   * beneath the skin in the skins overlay; absent for unlocked/default
   * skins). For achievement reward skins the server composes it from the
   * linked achievement (hidden ones are redacted); condition skins carry
   * it in the shared catalogue; placeholder skins get a generic "coming
   * soon" note.
   */
  unlockCriteria?: string;
  /**
   * Current progress toward the unlock criteria (locked skins with
   * MEASURABLE progress only — achievement progress or condition
   * progress; one-shot conditions and placeholder skins omit it).
   */
  unlockProgress?: number;
  /** The progress value at which the skin unlocks (same units as unlockProgress). */
  unlockTarget?: number;
}

/**
 * Response to GetSkinsRequest (and the confirmation sent after a
 * successful SetSkinRequest): the full skin catalogue with per-user
 * `unlocked`/`selected` state. Dynamic by construction — adding a skin or
 * a class to the shared SkinDefinitions automatically extends this list.
 */
export interface SkinsResponse extends ServerMessage {
  messageType: 'SkinsResponse';
  skins: SkinInfo[];
}

/**
 * Error response for invalid or rejected client messages.
 */
export interface ErrorResponse extends ServerMessage {
  messageType: 'ErrorResponse';
  error: string;
  originalMessageType?: string; // The message type that caused the error
}

// ─── Validation Helpers ──────────────────────────────────────────────────────

/**
 * Validates that a client message has the required base fields.
 */
export function validateClientMessage(msg: unknown): msg is ClientMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as Record<string, unknown>;
  if (typeof m.version !== 'string') return false;
  if (typeof m.messageType !== 'string') return false;
  if (!isVersionCompatible(m.version, PROTOCOL_VERSION)) {
    throw new Error(`Incompatible protocol version: "${m.version}"`);
  }
  return true;
}

/**
 * Validates that a server message has the required base fields.
 */
export function validateServerMessage(msg: unknown): msg is ServerMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as Record<string, unknown>;
  if (typeof m.version !== 'string') return false;
  if (typeof m.messageType !== 'string') return false;
  if (!isVersionCompatible(m.version, PROTOCOL_VERSION)) {
    throw new Error(`Incompatible protocol version: "${m.version}"`);
  }
  return true;
}

/**
 * Type-guard for AuthenticateRequest.
 */
export function isAuthenticateRequest(msg: ClientMessage): msg is AuthenticateRequest {
  return msg.messageType === 'AuthenticateRequest' &&
    typeof (msg as AuthenticateRequest).token === 'string';
}

/**
 * Type-guard for JoinQueueRequest.
 */
export function isJoinQueueRequest(msg: ClientMessage): msg is JoinQueueRequest {
  return msg.messageType === 'JoinQueueRequest' &&
    typeof (msg as JoinQueueRequest).playerId === 'string';
}

/**
 * START SOLO MATCH (task 191 — the landing page's Singleplayer option):
 * the server creates an 8-player match (the requesting player + 7 bots)
 * INSTANTLY — no queue, no bot-fill wait, no lobby. The match starts
 * exactly like any other (onMatchStart → the normal MatchStarted
 * broadcast + observers); there is no dedicated response message.
 */
export interface StartSoloMatchRequest extends ClientMessage {
  messageType: 'StartSoloMatchRequest';
  playerId: string;
}

/**
 * Type-guard for StartSoloMatchRequest.
 */
export function isStartSoloMatchRequest(msg: ClientMessage): msg is StartSoloMatchRequest {
  return msg.messageType === 'StartSoloMatchRequest' &&
    typeof (msg as StartSoloMatchRequest).playerId === 'string';
}

/**
 * Type-guard for LeaveQueueRequest.
 */
export function isLeaveQueueRequest(msg: ClientMessage): msg is LeaveQueueRequest {
  return msg.messageType === 'LeaveQueueRequest' &&
    typeof (msg as LeaveQueueRequest).playerId === 'string';
}

/**
 * Type-guard for PlayMinionRequest.
 */
export function isPlayMinionRequest(msg: ClientMessage): msg is PlayMinionRequest {
  return msg.messageType === 'PlayMinionRequest' &&
    typeof (msg as PlayMinionRequest).matchId === 'string' &&
    typeof (msg as PlayMinionRequest).playerId === 'string' &&
    typeof (msg as PlayMinionRequest).minionInstanceId === 'string' &&
    (typeof (msg as PlayMinionRequest).targetPosition === 'undefined' ||
     typeof (msg as PlayMinionRequest).targetPosition === 'number');
}

/**
 * Type-guard for SellMinionRequest.
 */
export function isSellMinionRequest(msg: ClientMessage): msg is SellMinionRequest {
  return msg.messageType === 'SellMinionRequest' &&
    typeof (msg as SellMinionRequest).matchId === 'string' &&
    typeof (msg as SellMinionRequest).playerId === 'string' &&
    typeof (msg as SellMinionRequest).minionInstanceId === 'string';
}

/**
 * Type-guard for GiveMinionRequest.
 */
export function isGiveMinionRequest(msg: ClientMessage): msg is GiveMinionRequest {
  return msg.messageType === 'GiveMinionRequest' &&
    typeof (msg as GiveMinionRequest).matchId === 'string' &&
    typeof (msg as GiveMinionRequest).playerId === 'string' &&
    typeof (msg as GiveMinionRequest).minionInstanceId === 'string';
}

/**
 * Type-guard for PlaySpellRequest.
 */
export function isPlaySpellRequest(msg: ClientMessage): msg is PlaySpellRequest {
  return msg.messageType === 'PlaySpellRequest' &&
    typeof (msg as PlaySpellRequest).matchId === 'string' &&
    typeof (msg as PlaySpellRequest).playerId === 'string' &&
    typeof (msg as PlaySpellRequest).spellInstanceId === 'string' &&
    (typeof (msg as PlaySpellRequest).targetInstanceId === 'undefined' ||
     typeof (msg as PlaySpellRequest).targetInstanceId === 'string');
}

/**
 * Type-guard for RepositionMinionsRequest.
 */
export function isRepositionMinionsRequest(msg: ClientMessage): msg is RepositionMinionsRequest {
  return msg.messageType === 'RepositionMinionsRequest' &&
    typeof (msg as RepositionMinionsRequest).matchId === 'string' &&
    typeof (msg as RepositionMinionsRequest).playerId === 'string' &&
    Array.isArray((msg as RepositionMinionsRequest).newOrder);
}

/**
 * Type-guard for BuyCardRequest.
 */
export function isBuyCardRequest(msg: ClientMessage): msg is BuyCardRequest {
  return msg.messageType === 'BuyCardRequest' &&
    typeof (msg as BuyCardRequest).matchId === 'string' &&
    typeof (msg as BuyCardRequest).playerId === 'string';
}

/**
 * Type-guard for UpgradeMinionRequest.
 */
export function isUpgradeMinionRequest(msg: ClientMessage): msg is UpgradeMinionRequest {
  return msg.messageType === 'UpgradeMinionRequest' &&
    typeof (msg as UpgradeMinionRequest).matchId === 'string' &&
    typeof (msg as UpgradeMinionRequest).playerId === 'string' &&
    typeof (msg as UpgradeMinionRequest).minionInstanceId === 'string' &&
    typeof (msg as UpgradeMinionRequest).branchId === 'string' &&
    (typeof (msg as UpgradeMinionRequest).selectedAbilityId === 'undefined' ||
     typeof (msg as UpgradeMinionRequest).selectedAbilityId === 'string');
}

/**
 * Type-guard for ReconnectRequest.
 */
export function isReconnectRequest(msg: ClientMessage): msg is ReconnectRequest {
  return msg.messageType === 'ReconnectRequest' &&
    typeof (msg as ReconnectRequest).playerId === 'string' &&
    typeof (msg as ReconnectRequest).matchId === 'string';
}

/**
 * Type-guard for GetUpgradeOptionsRequest.
 */
export function isGetUpgradeOptionsRequest(msg: ClientMessage): msg is GetUpgradeOptionsRequest {
  return msg.messageType === 'GetUpgradeOptionsRequest' &&
    typeof (msg as GetUpgradeOptionsRequest).matchId === 'string' &&
    typeof (msg as GetUpgradeOptionsRequest).playerId === 'string' &&
    typeof (msg as GetUpgradeOptionsRequest).minionInstanceId === 'string';
}

/**
 * Type-guard for GetAchievementsRequest.
 */
export function isGetAchievementsRequest(msg: ClientMessage): msg is GetAchievementsRequest {
  return msg.messageType === 'GetAchievementsRequest' &&
    typeof (msg as GetAchievementsRequest).playerId === 'string';
}

/**
 * Type-guard for GetStatsRequest.
 */
export function isGetStatsRequest(msg: ClientMessage): msg is GetStatsRequest {
  return msg.messageType === 'GetStatsRequest' &&
    typeof (msg as GetStatsRequest).playerId === 'string';
}

/**
 * Type-guard for GetSkinsRequest.
 */
export function isGetSkinsRequest(msg: ClientMessage): msg is GetSkinsRequest {
  return msg.messageType === 'GetSkinsRequest' &&
    typeof (msg as GetSkinsRequest).playerId === 'string';
}

/**
 * Type-guard for GetToplistRequest.
 */
export function isGetToplistRequest(msg: ClientMessage): msg is GetToplistRequest {
  return msg.messageType === 'GetToplistRequest' &&
    typeof (msg as GetToplistRequest).playerId === 'string';
}

/**
 * Type-guard for SetSkinRequest.
 */
export function isSetSkinRequest(msg: ClientMessage): msg is SetSkinRequest {
  return msg.messageType === 'SetSkinRequest' &&
    typeof (msg as SetSkinRequest).playerId === 'string' &&
    typeof (msg as SetSkinRequest).minionType === 'string' &&
    typeof (msg as SetSkinRequest).skinId === 'string';
}

/**
 * Type-guard for BuyRuneRequest.
 */
export function isBuyRuneRequest(msg: ClientMessage): msg is BuyRuneRequest {
  return msg.messageType === 'BuyRuneRequest' &&
    typeof (msg as BuyRuneRequest).matchId === 'string' &&
    typeof (msg as BuyRuneRequest).playerId === 'string' &&
    typeof (msg as BuyRuneRequest).runeId === 'string';
}

/**
 * Type-guard for MarkReadyRequest.
 */
export function isMarkReadyRequest(msg: ClientMessage): msg is MarkReadyRequest {
  return msg.messageType === 'MarkReadyRequest' &&
    typeof (msg as MarkReadyRequest).matchId === 'string' &&
    typeof (msg as MarkReadyRequest).playerId === 'string' &&
    typeof (msg as MarkReadyRequest).ready === 'boolean';
}

/**
 * Type-guard for ResignMatchRequest.
 */
export function isResignMatchRequest(msg: ClientMessage): msg is ResignMatchRequest {
  return msg.messageType === 'ResignMatchRequest' &&
    typeof (msg as ResignMatchRequest).matchId === 'string' &&
    typeof (msg as ResignMatchRequest).playerId === 'string';
}

/**
 * Type-guard for CreateCoopLobbyRequest.
 */
export function isCreateCoopLobbyRequest(msg: ClientMessage): msg is CreateCoopLobbyRequest {
  return msg.messageType === 'CreateCoopLobbyRequest' &&
    typeof (msg as CreateCoopLobbyRequest).playerId === 'string';
}

/**
 * Type-guard for JoinCoopLobbyRequest.
 */
export function isJoinCoopLobbyRequest(msg: ClientMessage): msg is JoinCoopLobbyRequest {
  return msg.messageType === 'JoinCoopLobbyRequest' &&
    typeof (msg as JoinCoopLobbyRequest).playerId === 'string' &&
    typeof (msg as JoinCoopLobbyRequest).code === 'string';
}

/**
 * Type-guard for LeaveCoopLobbyRequest.
 */
export function isLeaveCoopLobbyRequest(msg: ClientMessage): msg is LeaveCoopLobbyRequest {
  return msg.messageType === 'LeaveCoopLobbyRequest' &&
    typeof (msg as LeaveCoopLobbyRequest).playerId === 'string';
}

/**
 * Type-guard for SelectCoopMissionRequest.
 */
export function isSelectCoopMissionRequest(msg: ClientMessage): msg is SelectCoopMissionRequest {
  return msg.messageType === 'SelectCoopMissionRequest' &&
    typeof (msg as SelectCoopMissionRequest).playerId === 'string' &&
    typeof (msg as SelectCoopMissionRequest).lobbyCode === 'string' &&
    typeof (msg as SelectCoopMissionRequest).missionId === 'string';
}

/**
 * Type-guard for SelectCoopSideRequest.
 */
export function isSelectCoopSideRequest(msg: ClientMessage): msg is SelectCoopSideRequest {
  return msg.messageType === 'SelectCoopSideRequest' &&
    typeof (msg as SelectCoopSideRequest).playerId === 'string' &&
    typeof (msg as SelectCoopSideRequest).lobbyCode === 'string' &&
    ((msg as SelectCoopSideRequest).side === 'left' || (msg as SelectCoopSideRequest).side === 'right');
}

/**
 * Type-guard for StartCoopLobbyRequest.
 */
export function isStartCoopLobbyRequest(msg: ClientMessage): msg is StartCoopLobbyRequest {
  return msg.messageType === 'StartCoopLobbyRequest' &&
    typeof (msg as StartCoopLobbyRequest).playerId === 'string' &&
    typeof (msg as StartCoopLobbyRequest).lobbyCode === 'string';
}

// ─── Unified lobby type guards ─────────────────────────────────────────────

export function isCreateLobbyRequest(msg: ClientMessage): msg is CreateLobbyRequest {
  return msg.messageType === 'CreateLobbyRequest' &&
    typeof (msg as CreateLobbyRequest).playerId === 'string';
}

export function isJoinLobbyRequest(msg: ClientMessage): msg is JoinLobbyRequest {
  return msg.messageType === 'JoinLobbyRequest' &&
    typeof (msg as JoinLobbyRequest).playerId === 'string' &&
    typeof (msg as JoinLobbyRequest).code === 'string';
}

export function isLeaveLobbyRequest(msg: ClientMessage): msg is LeaveLobbyRequest {
  return msg.messageType === 'LeaveLobbyRequest' &&
    typeof (msg as LeaveLobbyRequest).playerId === 'string';
}

export function isKickLobbyPlayerRequest(msg: ClientMessage): msg is KickLobbyPlayerRequest {
  return msg.messageType === 'KickLobbyPlayerRequest' &&
    typeof (msg as KickLobbyPlayerRequest).playerId === 'string' &&
    typeof (msg as KickLobbyPlayerRequest).lobbyId === 'string' &&
    typeof (msg as KickLobbyPlayerRequest).targetId === 'string';
}

export function isStartLobbyCountdownRequest(msg: ClientMessage): msg is StartLobbyCountdownRequest {
  return msg.messageType === 'StartLobbyCountdownRequest' &&
    typeof (msg as StartLobbyCountdownRequest).playerId === 'string' &&
    typeof (msg as StartLobbyCountdownRequest).lobbyId === 'string';
}

export function isGetLobbyStateRequest(msg: ClientMessage): msg is GetLobbyStateRequest {
  return msg.messageType === 'GetLobbyStateRequest' &&
    typeof (msg as GetLobbyStateRequest).playerId === 'string' &&
    typeof (msg as GetLobbyStateRequest).lobbyId === 'string';
}

/**
 * Known client message types (for routing/validation).
 */
export const CLIENT_MESSAGE_TYPES = [
  'AuthenticateRequest',
  'JoinQueueRequest',
  'StartSoloMatchRequest',
  'LeaveQueueRequest',
  'PlayMinionRequest',
  'SellMinionRequest',
  'GiveMinionRequest',
  'PlaySpellRequest',
  'RepositionMinionsRequest',
  'BuyCardRequest',
  'UpgradeMinionRequest',
  'GetUpgradeOptionsRequest',
  'ReconnectRequest',
  'GetAchievementsRequest',
  'GetStatsRequest',
  'GetSkinsRequest',
  'SetSkinRequest',
  'BuyRuneRequest',
  'MarkReadyRequest',
  'GetToplistRequest',
  'ResignMatchRequest',
  'CreateCoopLobbyRequest',
  'JoinCoopLobbyRequest',
  'LeaveCoopLobbyRequest',
  'SelectCoopMissionRequest',
  'SelectCoopSideRequest',
  'StartCoopLobbyRequest',
  'CreateLobbyRequest',
  'JoinLobbyRequest',
  'LeaveLobbyRequest',
  'KickLobbyPlayerRequest',
  'StartLobbyCountdownRequest',
  'GetLobbyStateRequest',
] as const;

/**
 * Known server message types (for routing/validation).
 */
export const SERVER_MESSAGE_TYPES = [
  'Authenticated',
  'AuthFailed',
  'QueueStatusUpdate',
  'CoopLobbyStateUpdate',
  'LobbyStateUpdate',
  'MatchStarted',
  'GameStateUpdate',
  'PreparationTimerUpdate',
  'ReadyStateUpdate',
  'CombatStarted',
  'CombatStream',
  'CombatAttackEvent',
  'CombatDamageEvent',
  'CombatDeathEvent',
  'CombatKillGoldEvent',
  'CombatAbilityEvent',
  'CombatResult',
  'RoundStarted',
  'RoundTransition',
  'GameOver',
  'SpectatorCombatSelected',
  'SitOutNotification',
  'ReconnectState',
  'UpgradeOptionsResponse',
  'AchievementsResponse',
  'AchievementUnlocked',
  'StatsResponse',
  'SkinsResponse',
  'ToplistResponse',
  'ErrorResponse',
] as const;

/**
 * Validates that a client message has a known messageType.
 */
export function isValidClientMessageType(messageType: string): boolean {
  return (CLIENT_MESSAGE_TYPES as readonly string[]).includes(messageType);
}

/**
 * Validates that a server message has a known messageType.
 */
export function isValidServerMessageType(messageType: string): boolean {
  return (SERVER_MESSAGE_TYPES as readonly string[]).includes(messageType);
}
